// frontend/src/pages/PaychequeDetail/useMutations.ts
// Async mutations for the paycheque detail page.
// Hook owns the API calls + loading flags; parent handles side effects.

import { useState } from "react";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";

interface SaveMemoResult {
  success: boolean;
  error?: string;
}

interface VoidPaychequeResult {
  success: boolean;
  error?: string;
}

export function usePaychequeDetailMutations() {
  const [savingMemo, setSavingMemo] = useState<boolean>(false);
  const [voiding, setVoiding] = useState<boolean>(false);

  /**
   * PATCH the paycheque's memo field.
   * Parent handles updating local state (so the pay stub reflects the new memo).
   */
  async function saveMemo(stubId: string, memo: string): Promise<SaveMemoResult> {
    setSavingMemo(true);
    try {
      const res = await apiFetch("/api/v1/payroll/paycheques/" + stubId, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ memo }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        return { success: false, error: err.detail || "Could not save memo" };
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    } finally {
      setSavingMemo(false);
    }
  }

  /**
   * POST to the void endpoint. On success, parent updates local status to "voided".
   */
  async function voidPaycheque(stubId: string, reason: string): Promise<VoidPaychequeResult> {
    setVoiding(true);
    try {
      const res = await apiFetch("/api/v1/payroll/paycheques/" + stubId + "/void", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        return { success: false, error: err.detail || "Could not void paycheque" };
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    } finally {
      setVoiding(false);
    }
  }

  return {
    saveMemo,
    voidPaycheque,
    savingMemo,
    voiding,
  };
}